import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Option } from '@interfaces/common/option';
import {
  ClientDealSummaryResponse,
  ClientDealViewResponse,
} from '@interfaces/partners/responses/client-deal.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientDealsHttpService } from '@services/http/partners/client-deals-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import {
  CLIENT_DEAL_STAGE_DESCRIPTIONS,
  CLIENT_DEAL_STAGE_LABELS,
  nextClientDealStage,
  toStageTimelineSteps,
} from '../client-deal-stage';
import {
  describeClientError,
  isTechnicalFailure,
} from '@src/app/shared/utils/client-error-message';
import { ClientSignaturesCardComponent } from '../components/client-signatures-card/client-signatures-card.component';
import { ClientStageTimelineComponent } from '../components/client-stage-timeline/client-stage-timeline.component';

/**
 * Comercial › Minha operação › detalhe (Visão do Cliente, card 3461; protótipo `v2/cliente.jsx`).
 *
 * "Sua jornada com a Legatus" (protótipo aprovado em 19/10/2026): o usuário da empresa só acompanha.
 * Vê as 8 etapas numa linha do tempo vertical, com o que acontece em cada uma e o texto da atual, o
 * próximo passo e o NDA + carta SCR (com o "Assinar agora" para quem ainda não assinou, decisão de
 * 16/10/2026 mantida pelo Breno em 19/10/2026). Não envia nem vê documento. Fica no layout da
 * plataforma, não num shell separado.
 *
 * ⚠️ Fora desta entrega (decisão de 16/10/2026): "Documentos solicitados", "Seu time na Legatus" e a
 * frase "você será avisado por e-mail". O backend também não devolve parceiro, valor, gates nem
 * linha do tempo interna.
 */
@Component({
  selector: 'app-client-deal-detail',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    RouterLink,
    ClientStageTimelineComponent,
    ClientSignaturesCardComponent,
  ],
  templateUrl: './client-deal-detail.component.html',
  styleUrl: './client-deal-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientDealDetailComponent implements OnInit {
  private readonly clientDealsService = inject(ClientDealsHttpService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Minha operação', routerLink: '/dashboard/commercial/client' },
  ];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);

  /** "Tentar de novo" só faz sentido em falha técnica; erro de negócio se repetiria igual. */
  readonly canRetry = signal(false);
  readonly view = signal<ClientDealViewResponse | null>(null);
  readonly signing = signal(false);
  readonly signLinkOpened = signal(false);

  readonly steps = computed(() => {
    const view = this.view();
    return view ? toStageTimelineSteps(view.steps, view.stage) : [];
  });

  /** Painel "Próximo passo": a etapa seguinte e o que acontece nela; `null` na última etapa. */
  readonly nextStep = computed(() => {
    const view = this.view();
    const next = view ? nextClientDealStage(view.stage, view.steps) : null;
    return next
      ? { label: CLIENT_DEAL_STAGE_LABELS[next], description: CLIENT_DEAL_STAGE_DESCRIPTIONS[next] }
      : null;
  });

  /** Operações das empresas do usuário, na ordem de `GET commercial/client/deals`; alimenta o seletor. */
  private readonly deals = signal<ClientDealSummaryResponse[]>([]);
  private readonly currentDealId = signal<string | null>(null);

  /**
   * Seletor "Operação #N" (caso raro: empresa com mais de um deal), na ordem de `GET commercial/client/deals`.
   * Vazio com uma operação só ou se a lista não carregou, e aí o seletor não aparece.
   */
  readonly dealOptions = computed<Option[]>(() => {
    const deals = this.deals();
    if (deals.length < 2) return [];
    return deals.map(deal => ({
      label: `Operação #${deal.sequentialId}`,
      value: deal.id,
    }));
  });

  /** Id da operação aberta, para o seletor. */
  readonly selectedDealId = this.currentDealId.asReadonly();

  private dealId: string | null = null;
  private loadSubscription: Subscription | null = null;

  ngOnInit(): void {
    // paramMap, e não snapshot: o seletor de operação e o voltar do navegador reaproveitam este componente.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.dealId = params.get('id');
      this.currentDealId.set(this.dealId);
      this.view.set(null);
      this.signLinkOpened.set(false);
      this.load();
    });
    this.loadDeals();
  }

  /**
   * Carrega a lista das operações uma vez, só para o seletor de operação. Falhar aqui não atrapalha a tela: o
   * seletor simplesmente não aparece.
   */
  private loadDeals(): void {
    this.clientDealsService
      .getMyDeals()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.deals.set(response ?? []),
        error: () => this.deals.set([]),
      });
  }

  /** Troca de operação pelo seletor: o `paramMap` recarrega a tela com a operação escolhida. */
  openDeal(dealId: string | null): void {
    if (!dealId || dealId === this.currentDealId()) return;
    this.router.navigate(['/dashboard/commercial/client', dealId]);
  }

  load(): void {
    const dealId = this.dealId;
    if (!dealId) return;

    this.loadSubscription?.unsubscribe();
    this.loading.set(true);
    this.errorMessage.set(null);

    this.loadSubscription = this.clientDealsService
      .getDeal(dealId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.view.set(response);
          this.loading.set(false);
          if (!response) {
            this.errorMessage.set(
              'Não encontramos esta operação entre as da sua empresa. Clique em Voltar para Minha operação para abrir a sua operação; se houver mais de uma, troque pelo seletor no topo.'
            );
          }
        },
        error: error => {
          this.loading.set(false);
          this.canRetry.set(isTechnicalFailure(error));
          this.errorMessage.set(
            describeClientError(
              error,
              'Não conseguimos abrir esta operação agora. Confira a sua conexão e clique em Tentar de novo; se continuar assim, volte para Minha operação.'
            )
          );
        },
      });
  }

  /**
   * "Assinar agora": busca o link do provedor de assinatura para o usuário logado e abre numa aba nova.
   *
   * A aba é aberta **antes** da chamada, em branco, e só recebe o endereço quando o link chega: aberta
   * depois da resposta, ela já não conta como clique da pessoa e o bloqueador de pop-up recusa.
   */
  signTerm(): void {
    const dealId = this.view()?.id;
    if (!dealId || this.signing()) return;

    const signTab = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    this.signing.set(true);

    this.clientDealsService
      .getSignLink(dealId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.signing.set(false);
          const url = response?.url;
          if (!url) {
            signTab?.close();
            this.toast.showToastError(
              'Não recebemos o link de assinatura',
              'Clique em Assinar agora de novo; se continuar assim, fale com o time da Legatus.'
            );
            return;
          }

          if (!signTab) {
            this.toast.showToastError(
              'O navegador bloqueou a nova aba',
              'Permita pop-ups para este site e clique em Assinar agora de novo.'
            );
            return;
          }

          // A aba de assinatura não precisa (nem deve) conseguir mexer nesta.
          signTab.opener = null;
          signTab.location.href = url;
          this.signLinkOpened.set(true);
        },
        error: error => {
          this.signing.set(false);
          signTab?.close();
          this.toast.showToastError(
            'Não foi possível abrir a assinatura',
            describeClientError(
              error,
              'Confira a sua conexão e clique em Assinar agora de novo; se continuar assim, fale com o time da Legatus.'
            )
          );
        },
      });
  }
}
